// AI Model Configuration

export interface ModelInfo {
  id: string;
  name: string;
  provider: 'xai' | 'anthropic' | 'openai' | 'google' | 'deepseek' | 'moonshot';
  description: string;
  contextWindow?: string;
  performance?: string;
}

export interface ModelCategory {
  name: string;
  models: ModelInfo[];
}

export const AI_MODELS: ModelCategory[] = [
  {
    name: 'xAI Models',
    models: [
      {
        id: 'x-ai/grok-code-fast-1',
        name: 'Grok Code Fast 1',
        provider: 'xai',
        description: '#1 on token usage leaderboard, optimized for agentic coding',
      },
      {
        id: 'x-ai/grok-4',
        name: 'Grok 4',
        provider: 'xai',
        description: 'Latest reasoning model',
        contextWindow: '256k',
      },
      {
        id: 'x-ai/grok-4-fast',
        name: 'Grok 4 Fast',
        provider: 'xai',
        description: 'Efficiency-tuned',
        contextWindow: '2M tokens',
      },
    ],
  },
  {
    name: 'Anthropic Claude Models',
    models: [
      {
        id: 'anthropic/claude-opus-4-6',
        name: 'Claude Opus 4.6',
        provider: 'anthropic',
        description: 'Most advanced Claude model with 1M context',
        contextWindow: '1M',
      },
      {
        id: 'anthropic/claude-sonnet-4-5',
        name: 'Claude Sonnet 4.5',
        provider: 'anthropic',
        description: '#2 on leaderboard, most advanced Sonnet for agents',
      },
      {
        id: 'anthropic/claude-opus-4.1',
        name: 'Claude Opus 4.1',
        provider: 'anthropic',
        description: 'Updated flagship',
        performance: '74.5% SWE-bench',
      },
      {
        id: 'anthropic/claude-opus-4',
        name: 'Claude Opus 4',
        provider: 'anthropic',
        description: "World's best coding model at release",
        performance: '72.5% SWE-bench',
      },
      {
        id: 'anthropic/claude-sonnet-4',
        name: 'Claude Sonnet 4',
        provider: 'anthropic',
        description: 'State-of-the-art',
        performance: '72.7% SWE-bench',
      },
      {
        id: 'anthropic/claude-3.7-sonnet',
        name: 'Claude 3.7 Sonnet',
        provider: 'anthropic',
        description: 'Hybrid reasoning model',
      },
      {
        id: 'anthropic/claude-3-5-sonnet',
        name: 'Claude 3.5 Sonnet',
        provider: 'anthropic',
        description: 'Popular for agentic tasks',
      },
      {
        id: 'anthropic/claude-haiku-4.5',
        name: 'Claude Haiku 4.5',
        provider: 'anthropic',
        description: 'Fastest model with extended thinking',
      },
    ],
  },
  {
    name: 'OpenAI Models',
    models: [
      {
        id: 'openai/gpt-5',
        name: 'GPT-5',
        provider: 'openai',
        description: 'Most advanced with step-by-step reasoning',
      },
      {
        id: 'openai/gpt-5.1',
        name: 'GPT-5.1',
        provider: 'openai',
        description: 'Latest frontier with adaptive reasoning',
      },
      {
        id: 'openai/gpt-5-nano',
        name: 'GPT-5 Nano',
        provider: 'openai',
        description: 'Optimized for ultra-low latency',
      },
      {
        id: 'openai/o3',
        name: 'O3',
        provider: 'openai',
        description: 'Reasoning model for math, science, coding',
      },
      {
        id: 'openai/o3-mini',
        name: 'O3 Mini',
        provider: 'openai',
        description: 'Cost-efficient reasoning',
      },
      {
        id: 'openai/gpt-4.1',
        name: 'GPT-4.1',
        provider: 'openai',
        description: '1M token context flagship',
        contextWindow: '1M',
      },
      {
        id: 'openai/gpt-4o',
        name: 'GPT-4o',
        provider: 'openai',
        description: 'Popular multimodal model',
      },
    ],
  },
  {
    name: 'Google Gemini Models',
    models: [
      {
        id: 'google/gemini-2.5-pro',
        name: 'Gemini 2.5 Pro',
        provider: 'google',
        description: 'State-of-the-art with thinking capabilities',
      },
      {
        id: 'google/gemini-3-pro-preview',
        name: 'Gemini 3 Pro Preview',
        provider: 'google',
        description: 'Featured model for agentic workflows',
      },
      {
        id: 'google/gemini-3-flash-preview',
        name: 'Gemini 3 Flash Preview',
        provider: 'google',
        description: 'Latest Flash, optimized for speed',
      },
      {
        id: 'google/gemini-1.5-pro',
        name: 'Gemini 1.5 Pro',
        provider: 'google',
        description: 'Advanced multimodal',
      },
    ],
  },
  {
    name: 'Moonshot / Kimi Models',
    models: [
      {
        id: 'moonshotai/kimi-k2.5',
        name: 'Kimi K2.5',
        provider: 'moonshot',
        description: 'Multimodal with native function calling',
        contextWindow: '262k',
      },
      {
        id: 'moonshotai/kimi-k2-thinking',
        name: 'Kimi K2 Thinking',
        provider: 'moonshot',
        description: 'Advanced reasoning with tool use',
        contextWindow: '262k',
      },
    ],
  },
  {
    name: 'DeepSeek Models',
    models: [
      {
        id: 'deepseek/deepseek-r1',
        name: 'DeepSeek R1',
        provider: 'deepseek',
        description: 'Performance on par with o1, fully open',
      },
      {
        id: 'deepseek/deepseek-r1:free',
        name: 'DeepSeek R1 Free',
        provider: 'deepseek',
        description: 'Free tier version',
      },
      {
        id: 'deepseek/deepseek-v3.1',
        name: 'DeepSeek V3.1',
        provider: 'deepseek',
        description: '671B hybrid reasoning',
      },
    ],
  },
];

// Flatten all models for easy lookup
export const ALL_MODELS = AI_MODELS.flatMap((category) => category.models);

// Default LLM model ID (used when no model is explicitly selected)
export const DEFAULT_LLM_MODEL = 'moonshotai/kimi-k2.5';

// Get model by ID
export const getModelById = (id: string): ModelInfo | undefined => {
  return ALL_MODELS.find((model) => model.id === id);
};

// Get models by provider
export const getModelsByProvider = (provider: ModelInfo['provider']): ModelInfo[] => {
  return ALL_MODELS.filter((model) => model.provider === provider);
};
